import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, IconButton, Tooltip, Alert, Chip, Divider,
} from '@mui/material';
import { Home, Gavel, Print, Search } from '@mui/icons-material';
import axios from 'axios';
import CompanyHeader from './CompanyHeader';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

const Stat = ({ value, label, color }) => (
  <Card elevation={2} sx={{ borderRadius: 3, borderLeft: `4px solid ${color}` }}>
    <CardContent sx={{ py: 1.8 }}>
      <Typography variant="h4" fontWeight={800} color={color} lineHeight={1}>{value}</Typography>
      <Typography variant="body2" fontWeight={600} mt={0.5}>{label}</Typography>
    </CardContent>
  </Card>
);

const DisciplinaryReports = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [empNo, setEmpNo]   = useState('');
  const [statement, setStatement] = useState([]);
  const [error, setError]   = useState('');

  const loadSummary = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/disciplinary/reports/summary`, { headers: hdrs() }); setSummary(r.data); }
    catch (e) { setError(e.response?.data?.error || 'Failed'); }
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadStatement = async () => {
    if (!empNo) return;
    try { const r = await axios.get(`${API}/api/disciplinary/reports/statement?employeeNo=${empNo}`, { headers: hdrs() }); setStatement(r.data); }
    catch (e) { setError(e.response?.data?.error || 'Failed'); }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, mb: 4, px: 2 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1} className="no-print">
            <Box display="flex" alignItems="center" gap={1}><Gavel color="primary" /><Typography variant="h5" color="primary">Disciplinary Reports</Typography></Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Print"><IconButton onClick={() => window.print()}><Print /></IconButton></Tooltip>
              <Tooltip title="Cases"><IconButton onClick={() => navigate('/hr/disciplinary')}><Home /></IconButton></Tooltip>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <CompanyHeader title="Disciplinary Report" />

          {summary && (
            <>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} sm={3}><Stat value={summary.total} label="Total Cases" color="#1565c0" /></Grid>
                <Grid item xs={6} sm={3}><Stat value={summary.open} label="Open / Ongoing" color="#e65100" /></Grid>
                <Grid item xs={6} sm={3}><Stat value={summary.gross} label="Gross Misconduct" color="#c62828" /></Grid>
                <Grid item xs={6} sm={3}><Stat value={summary.terminated} label="Terminated / Dismissed" color="#37474f" /></Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>Cases by Offence Type</Typography>
                  <Table size="small">
                    <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}><TableCell><strong>Type</strong></TableCell><TableCell align="center"><strong>Count</strong></TableCell></TableRow></TableHead>
                    <TableBody>
                      {(summary.byType || []).length === 0 ? <TableRow><TableCell colSpan={2} align="center"><Typography variant="caption" color="text.secondary">No data</Typography></TableCell></TableRow>
                        : summary.byType.map(t => <TableRow key={t.type}><TableCell>{t.type}</TableCell><TableCell align="center">{t.count}</TableCell></TableRow>)}
                    </TableBody>
                  </Table>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>Measures Taken</Typography>
                  <Table size="small">
                    <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}><TableCell><strong>Measure</strong></TableCell><TableCell align="center"><strong>Count</strong></TableCell></TableRow></TableHead>
                    <TableBody>
                      {(summary.byMeasure || []).length === 0 ? <TableRow><TableCell colSpan={2} align="center"><Typography variant="caption" color="text.secondary">No data</Typography></TableCell></TableRow>
                        : summary.byMeasure.map(m => <TableRow key={m.measure}><TableCell>{m.measure}</TableCell><TableCell align="center">{m.count}</TableCell></TableRow>)}
                    </TableBody>
                  </Table>
                </Grid>
              </Grid>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Individual disciplinary statement */}
          <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Individual Staff Disciplinary Statement</Typography>
          <Box display="flex" gap={1.5} mb={2} className="no-print">
            <TextField size="small" label="Employee No" value={empNo} onChange={e => setEmpNo(e.target.value)} sx={{ minWidth: 180 }} onKeyDown={e => e.key === 'Enter' && loadStatement()} />
            <IconButton color="primary" onClick={loadStatement}><Search /></IconButton>
          </Box>
          {statement.length > 0 && (
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Case</strong></TableCell><TableCell><strong>Offence</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell><TableCell><strong>Current Stage</strong></TableCell>
                <TableCell><strong>Outcome</strong></TableCell><TableCell><strong>Status</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {statement.map(c => (
                  <TableRow key={c._id}>
                    <TableCell>{c.caseNo}</TableCell>
                    <TableCell>{c.offenceType}{c.offenceCategory ? ` — ${c.offenceCategory}` : ''}</TableCell>
                    <TableCell>{fmtDate(c.offenceDate)}</TableCell>
                    <TableCell>{c.currentStage || '—'}</TableCell>
                    <TableCell>{c.outcome || '—'}</TableCell>
                    <TableCell><Chip label={c.status} size="small" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DisciplinaryReports;
